import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import DateRangeFilter from "./shared/DateRangeFilter";
import KpiCards from "./shared/KpiCards";
import AnalyticsChart from "./shared/AnalyticsChart";
import AnalyticsMetricList from "./shared/AnalyticsMetricList";
import AnalyticsRankedAxisTick from "./shared/AnalyticsRankedAxisTick";
import AnalyticsWorkspaceHero from "./shared/AnalyticsWorkspaceHero";
import useAnalyticsDashboardData from "./shared/useAnalyticsDashboardData";
import {
  ANALYTICS_SERIES_COLORS,
  buildDistributionSummary,
  DistributionTooltip,
  formatNumber,
  formatPercentage,
  formatRangeLabel,
  getKpiValue,
} from "./shared/analyticsUtils";

function DoctorsSection(props) {
  const { data, loading, currentStartDate, currentEndDate, handleDateChange } = useAnalyticsDashboardData({
    dataUrl: props.dataUrl,
    startDate: props.startDate,
    endDate: props.endDate,
  });
  const locale = props.locale || "en";
  const labels = props.labels;
  const topDoctors = data?.top_doctors || [];
  const specialityBreakdown = data?.speciality_breakdown || [];
  const totalAppointments = getKpiValue(data?.kpis, "total_appointments");
  const activeDoctors = getKpiValue(data?.kpis, "active_doctors");
  const totalDoctors = getKpiValue(data?.kpis, "total_doctors");
  const averagePerDoctor = getKpiValue(data?.kpis, "avg_per_doctor");
  const topDoctor = topDoctors[0];
  const coverageRate = totalDoctors > 0 ? Math.round((activeDoctors / totalDoctors) * 100) : 0;
  const selectedRangeLabel = formatRangeLabel(currentStartDate, currentEndDate, locale);
  const loadingLabel = labels.loading || "...";

  const topDoctorHint = topDoctor?.subtitle
    ? `${topDoctor.subtitle} • ${formatNumber(topDoctor.value, locale)} ${labels.appointmentsUnit}`
    : topDoctor ? `${formatNumber(topDoctor.value, locale)} ${labels.appointmentsUnit}` : null;

  const heroItems = [
    {
      label: labels.selectedWindow,
      value: selectedRangeLabel || loadingLabel,
    },
    {
      label: labels.totalInWindow,
      value: loading ? loadingLabel : formatNumber(totalAppointments, locale),
      hint: labels.appointmentsUnit,
    },
    {
      label: labels.activeDoctors,
      value: loading ? loadingLabel : formatNumber(activeDoctors, locale),
      hint: labels.doctorsUnit,
    },
    {
      label: labels.topDoctor,
      value: loading ? loadingLabel : topDoctor?.name || labels.noData,
      hint: loading ? null : topDoctorHint,
    },
  ];

  const filterSummaryItems = [
    {
      label: labels.topDoctor,
      value: loading ? loadingLabel : topDoctor?.name || labels.noData,
      hint: loading ? null : topDoctorHint,
    },
    {
      label: labels.averagePerDoctor,
      value: loading ? loadingLabel : formatNumber(averagePerDoctor, locale),
      hint: labels.appointmentsUnit,
    },
    {
      label: labels.doctorCoverage,
      value: loading ? loadingLabel : formatPercentage(coverageRate, locale),
      hint: loading ? null : `${formatNumber(activeDoctors, locale)} / ${formatNumber(totalDoctors, locale)} ${labels.doctorsUnit}`,
    },
  ];

  const spotlightItems = [
    {
      label: labels.topDoctor,
      value: topDoctor?.name || labels.noData,
      hint: topDoctorHint,
      layout: "featured",
    },
    {
      label: labels.averagePerDoctor,
      value: formatNumber(averagePerDoctor, locale),
      hint: labels.appointmentsUnit,
    },
    {
      label: labels.doctorCoverage,
      value: formatPercentage(coverageRate, locale),
      hint: `${formatNumber(activeDoctors, locale)} / ${formatNumber(totalDoctors, locale)} ${labels.doctorsUnit}`,
    },
  ];

  const rankSummary = buildDistributionSummary(topDoctors, locale, labels.appointmentsUnit, {
    labelKey: "name",
    hintFormatter: (item, numberFormatter) => item.subtitle
      ? `${item.subtitle} • ${numberFormatter.format(item.value)} ${labels.appointmentsUnit}`
      : `${numberFormatter.format(item.value)} ${labels.appointmentsUnit}`,
  });
  const specialityChartHeight = Math.max(320, specialityBreakdown.length * 44);

  return (
    <div className="analytics-workspace">
      <AnalyticsWorkspaceHero
        eyebrow={labels.heroEyebrow}
        title={labels.heroTitle}
        description={labels.heroDescription}
        items={heroItems}
      />

      <DateRangeFilter
        startDate={currentStartDate}
        endDate={currentEndDate}
        labels={labels}
        onChange={handleDateChange}
        title={labels.filterTitle}
        description={labels.filterDescription}
        rangeLabel={selectedRangeLabel}
        summaryItems={filterSummaryItems}
      />

      <KpiCards kpis={data?.kpis} loading={loading} periodLabel={labels.vsPreviousPeriod} locale={locale} />

      <div className="row">
        <div className="col-12 mb-4">
          <AnalyticsChart
            title={labels.topDoctors}
            description={labels.topDoctorsDescription}
            loading={loading}
            isEmpty={!topDoctors.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-account"
            aside={<AnalyticsMetricList items={spotlightItems} emptyLabel={labels.noData} />}
            footer={<AnalyticsMetricList items={rankSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={360}
          >
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={topDoctors} layout="vertical" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.24)" />
                <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                <YAxis dataKey="name" type="category" width={180} tick={<AnalyticsRankedAxisTick maxLength={26} />} tickLine={false} axisLine={false} />
                <Tooltip
                  content={<DistributionTooltip locale={locale} unitLabel={labels.appointmentsUnit} labelKey="name" subtitleKey="subtitle" />}
                />
                <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                  {topDoctors.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={ANALYTICS_SERIES_COLORS[index % ANALYTICS_SERIES_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <AnalyticsChart
            eyebrow={labels.allTime}
            title={labels.specialityBreakdown}
            description={labels.specialityBreakdownDescription}
            loading={loading}
            isEmpty={!specialityBreakdown.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-label"
            loadingHeight={specialityChartHeight}
          >
            <ResponsiveContainer width="100%" height={specialityChartHeight}>
              <BarChart data={specialityBreakdown} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.24)" />
                <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                <YAxis dataKey="label" type="category" width={220} tick={<AnalyticsRankedAxisTick maxLength={32} />} tickLine={false} axisLine={false} />
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.doctorsUnit} />} />
                <Bar
                  data={specialityBreakdown}
                  dataKey="value"
                  radius={[0, 10, 10, 0]}
                >
                  {specialityBreakdown.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={ANALYTICS_SERIES_COLORS[index % ANALYTICS_SERIES_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>
    </div>
  );
}

export default DoctorsSection;
